/**
 * Step-project a sparse external time series (FNO option snapshots, funding
 * settlements, regime change-points …) onto the chart's candle grid.
 *
 * Each bar gets the LAST sample that occurred BEFORE the bar's close, i.e. during
 * or before the bar — "value as-of bar close". This is causal and non-repainting:
 * once a bar closes, the set of samples inside it never changes. The FORMING bar
 * carries the latest known sample, so the right-edge value is identical on every
 * timeframe. (The previous convention — last sample at-or-before the bar's OPEN —
 * made the right edge up to one full bar stale: a 4H chart could show a PCR from
 * 4 hours ago while the 1H chart showed the current one. These series drive live
 * decisions, so the freshest sample must always win at the right edge.)
 *
 * All times are in the chart's own time base (IST-shifted epoch seconds) — the
 * caller shifts both candles and samples onto the same grid before projecting.
 */
export function projectToBarClose(
  raw: Array<{ time: number; value: number }>,
  candleTimes: number[],
  opts?: {
    /** Bar Replay playhead (the last visible bar's START time). Bars after it are
     *  dropped; the playhead bar still sees samples up to its own close (it is
     *  fully formed at the playhead) but nothing beyond — no future leak. */
    maxTime?: number | null;
    /** Back-fill bars BEFORE the first sample with the first value, so a level
     *  line spans the whole chart (flat on the left). Default: skip those bars. */
    backfillFirst?: boolean;
    /** Cap the LAST bar's window at start+barSpan instead of +∞. Use when the
     *  candle array itself may be trimmed (e.g. replay slices), so samples newer
     *  than the visible right edge cannot leak onto it. Default false: the live
     *  forming bar always shows the freshest sample. */
    capLastBar?: boolean;
  },
): Array<{ time: number; value: number }> {
  const { maxTime = null, backfillFirst = false, capLastBar = false } = opts || {};
  if (!raw.length || !candleTimes.length) return [];
  const s = raw.slice().sort((a, b) => a.time - b.time);
  const n = candleTimes.length;
  // Median bar spacing — closes the final bar's window when it must be bounded.
  let span = Infinity;
  if (n >= 2) {
    const diffs: number[] = [];
    for (let i = 1; i < Math.min(n, 50); i++) diffs.push(candleTimes[i] - candleTimes[i - 1]);
    diffs.sort((a, b) => a - b);
    span = diffs[Math.floor(diffs.length / 2)] || Infinity;
  }
  const out: Array<{ time: number; value: number }> = [];
  let j = -1;
  for (let i = 0; i < n; i++) {
    const ct = candleTimes[i];
    if (maxTime != null && ct > maxTime) break;
    // Half-open window [barStart, nextBarStart): a sample stamped exactly on a bar
    // boundary belongs to the bar that OPENS at that instant (funding settlements
    // aligned to bar starts apply going forward, not to the bar just closed).
    const end = i + 1 < n
      ? candleTimes[i + 1]
      : ((capLastBar || maxTime != null) && Number.isFinite(span) ? ct + span : Infinity);
    while (j + 1 < s.length && s[j + 1].time < end) j++;
    if (j >= 0) out.push({ time: ct, value: s[j].value });
    else if (backfillFirst) out.push({ time: ct, value: s[0].value });
  }
  return out;
}
